import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, User, Home, Users, ExternalLink, Info } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { SHIFT_PERIODS, autoClassifyPeriod } from './ShiftPeriodBadge';
import { useAppDialog } from '@/hooks/useAppDialog';
import { useStaffAvailabilityForShift, AvailabilityStatusBadge } from './StaffAvailabilityHelpers';

const SHIFT_TYPES = [
  { value: 'individual', label: '1:1 Support', icon: User, hint: 'One participant, one or more services.' },
  { value: 'sil', label: 'SIL House', icon: Home, hint: 'Group home: one staff supporting multiple residents.' },
  { value: 'centre', label: 'Centre Session', icon: Users, hint: 'Group day program at a venue.' },
];

const fundingPlanLabel = (p) => {
  if (!p) return '—';
  const name = p.plan_name || p.name || p.plan_number;
  if (name) return name;
  if (p.id) return `Plan #${String(p.id).slice(-6)}`;
  return '—';
};

const newActivityRow = (order, start, end) => ({
  _key: Math.random().toString(36).slice(2),
  participant_id: '',
  service_catalog_id: '',
  funding_plan_id: '',
  allocation_start_time: start || '09:00',
  allocation_end_time: end || '17:00',
  allocation_order: order,
});

export function CalendarCreateShiftModal({ open, onClose, onCreated, prefillDate, prefillStaffId, participants, services, staff }) {
  const { dangerConfirm } = useAppDialog();
  const navigate = useNavigate();
  const [shiftType, setShiftType] = useState('individual');
  const [form, setForm] = useState({
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    participant_id: '',           // for 1:1
    service_id: '',
    staff_id: '',
    shift_period: 'AUTO',
    notes: '',
    // SIL
    house_id: '',
    support_ratio: '1:3',
    linked_participants: [],
  });
  const [creating, setCreating] = useState(false);

  // Reset when re-opened
  useEffect(() => {
    if (open) {
      setShiftType('individual');
      setForm({
        date: prefillDate || format(new Date(), 'yyyy-MM-dd'),
        start_time: '09:00', end_time: '17:00',
        participant_id: '', service_id: '',
        staff_id: prefillStaffId || '',
        shift_period: 'AUTO', notes: '',
        house_id: '', support_ratio: '1:3', linked_participants: [],
      });
      setActivities([]);
      setHouses([]);
      setHouseResidents([]);
    }
  }, [open, prefillDate, prefillStaffId]);

  // Multi-service activity rows (Option B)
  const [activities, setActivities] = useState([]);
  const addActivity = useCallback(() => {
    setActivities(prev => {
      const row = newActivityRow(prev.length + 1, form.start_time, form.end_time);
      // In 1:1 mode, immediately bind the new row to the chosen participant
      // so the Funding-plan and Service dropdowns are usable from the first click.
      if (shiftType === 'individual' && form.participant_id) {
        row.participant_id = form.participant_id;
      }
      return [...prev, row];
    });
  }, [form.start_time, form.end_time, shiftType, form.participant_id]);
  const updateActivity = (key, patch) => setActivities(prev => prev.map(a => a._key === key ? { ...a, ...patch } : a));
  const removeActivity = (key) => setActivities(prev => prev.filter(a => a._key !== key));

  // SIL houses + residents (Option A + C)
  const [houses, setHouses] = useState([]);
  const [houseResidents, setHouseResidents] = useState([]);
  useEffect(() => {
    if (shiftType !== 'sil' || !open) return;
    (async () => {
      try {
        const res = await api.get('/roster/sil/houses');
        setHouses(res.data?.data || res.data?.houses || []);
      } catch (e) { /* non-blocking */ }
    })();
  }, [shiftType, open]);
  useEffect(() => {
    if (shiftType !== 'sil' || !form.house_id) { setHouseResidents([]); return; }
    (async () => {
      try {
        const res = await api.get(`/roster/sil/houses/${form.house_id}`);
        const residents = res.data?.data?.residents || res.data?.residents || [];
        setHouseResidents(residents);
        // auto-select all active residents by default
        const ids = residents.filter(r => r.status !== 'archived').map(r => r.participant_id || r.id).filter(Boolean);
        setForm(f => ({ ...f, linked_participants: ids }));
      } catch (e) { setHouseResidents([]); }
    })();
  }, [shiftType, form.house_id]);

  // Cache funding plans per participant so activity rows can resolve them
  const [planCache, setPlanCache] = useState({}); // {participant_id: [{id, plan_name, ...}]}
  const ensurePlans = useCallback(async (pid) => {
    if (!pid || planCache[pid]) return;
    try {
      const res = await api.get(`/funding/plans/by-participant/${pid}`);
      const plans = res.data?.data || res.data?.items || [];
      setPlanCache(prev => ({ ...prev, [pid]: plans }));
    } catch { setPlanCache(prev => ({ ...prev, [pid]: [] })); }
  }, [planCache]);

  // Cache per-participant funded service items (NDIS billing safety — only show services this participant is funded for).
  // Shape: { [participant_id]: [{ service_catalog_id, service_name, service_code, service_category, funding_plan_id, rate_per_unit }] }
  const [psiCache, setPsiCache] = useState({});
  const ensurePsi = useCallback(async (pid) => {
    if (!pid || psiCache[pid]) return;
    try {
      const res = await api.get(`/services/participant-items?participant_id=${pid}`);
      setPsiCache(prev => ({ ...prev, [pid]: res.data?.data || [] }));
    } catch { setPsiCache(prev => ({ ...prev, [pid]: [] })); }
  }, [psiCache]);

  // "Show all" escape hatch toggle (per dropdown context)
  const [showAllPrimary, setShowAllPrimary] = useState(false);
  const [showAllActivity, setShowAllActivity] = useState({}); // {activityKey: bool}

  // Compute the eligible services for a participant + (optional) funding plan.
  // 1. Filter to PSI rows for this participant
  // 2. If funding plan picked, further restrict to PSI rows on that plan
  // 3. Map back to service_catalog entries (so we keep id/name/code).
  // 4. Returns null when no participant context (caller falls back to full catalog).
  const fundedServices = useCallback((participantId, fundingPlanId) => {
    if (!participantId) return null;
    const items = psiCache[participantId];
    if (!Array.isArray(items)) return null;
    let rows = items;
    if (fundingPlanId) rows = rows.filter(r => r.funding_plan_id === fundingPlanId);
    const seen = new Set();
    const result = [];
    for (const r of rows) {
      if (seen.has(r.service_catalog_id)) continue;
      seen.add(r.service_catalog_id);
      const svc = services.find(s => s.id === r.service_catalog_id);
      if (svc) result.push(svc);
    }
    return result;
  }, [psiCache, services]);

  // When 1:1 participant selected, pre-fetch plans + PSI
  useEffect(() => { if (form.participant_id) { ensurePlans(form.participant_id); ensurePsi(form.participant_id); } }, [form.participant_id, ensurePlans, ensurePsi]);
  // When activity row participant_id changes, fetch plans + PSI for them
  useEffect(() => {
    activities.forEach(a => { if (a.participant_id) { ensurePlans(a.participant_id); ensurePsi(a.participant_id); } });
  }, [activities, ensurePlans, ensurePsi]);

  // Auto-bind activity participant_id to the 1:1 participant whenever it changes
  useEffect(() => {
    if (shiftType !== 'individual' || !form.participant_id) return;
    setActivities(prev => prev.map(a => a.participant_id === form.participant_id ? a : { ...a, participant_id: form.participant_id, funding_plan_id: a.participant_id === form.participant_id ? a.funding_plan_id : '' }));
  }, [shiftType, form.participant_id]);

  const autoPreview = form.shift_period === 'AUTO' ? autoClassifyPeriod(form.start_time, form.end_time) : null;

  // Live staff availability lookup
  const { statusMap, sortedStaff, enabled: availEnabled } = useStaffAvailabilityForShift(staff, {
    date: form.date,
    startTime: form.start_time,
    endTime: form.end_time,
  });

  // Effective participants list (1:1 = participants prop; SIL = selected residents only)
  const activityParticipants = useMemo(() => {
    if (shiftType === 'sil') {
      const idSet = new Set(form.linked_participants || []);
      return participants.filter(p => idSet.has(p.id));
    }
    if (shiftType === 'individual' && form.participant_id) {
      return participants.filter(p => p.id === form.participant_id);
    }
    return [];
  }, [shiftType, form.linked_participants, form.participant_id, participants]);

  const toggleResident = (pid) => {
    setForm(f => {
      const has = (f.linked_participants || []).includes(pid);
      return { ...f, linked_participants: has ? f.linked_participants.filter(x => x !== pid) : [...f.linked_participants, pid] };
    });
  };

  const validateBase = () => {
    if (!form.date || !form.start_time || !form.end_time) return 'Date, start, and end are required';
    if (!form.service_id) return 'Select the primary service';
    if (shiftType === 'individual' && !form.participant_id) return 'Select a participant';
    if (shiftType === 'sil') {
      if (!form.house_id) return 'Select a SIL house';
      if (!(form.linked_participants || []).length) return 'Tick at least one resident';
    }
    return null;
  };

  // Build the right payload + endpoint, then post activities
  const handleCreate = async () => {
    const err = validateBase();
    if (err) { toast.error(err); return; }
    setCreating(true);
    try {
      // Soft availability pre-check (only if assigning a single staff)
      if (form.staff_id && form.staff_id !== 'none') {
        try {
          const checkRes = await api.get(`/availability/check?user_id=${form.staff_id}&date=${form.date}&start_time=${form.start_time}&end_time=${form.end_time}`);
          const conflicts = checkRes.data?.data?.conflicts || [];
          if (conflicts.length > 0) {
            const labels = conflicts.map(c => c.label).join(', ');
            const ok = await dangerConfirm({
              title: 'Heads-up: availability conflict',
              description: `The selected staff has marked ${form.date} as ${labels}. Creating the shift with this assignment is allowed — please confirm.`,
              confirmLabel: 'Create anyway',
              cancelLabel: 'Pick someone else',
              variant: 'warning',
              testId: 'create-avail-conflict-confirm',
            });
            if (!ok) { setCreating(false); return; }
          }
        } catch { /* non-blocking */ }
      }

      let newShiftId = null;

      if (shiftType === 'sil') {
        // SIL house shift creation
        const payload = {
          house_id: form.house_id,
          service_id: form.service_id,
          shift_date: form.date,
          start_time: form.start_time,
          end_time: form.end_time,
          assigned_staff_ids: form.staff_id && form.staff_id !== 'none' ? [form.staff_id] : [],
          shift_type: form.shift_period === 'AUTO' ? 'SIL' : form.shift_period,
          notes: form.notes,
          support_ratio: form.support_ratio,
          support_type: 'sil',
          shift_context_type: 'accommodation',
          linked_house_id: form.house_id,
          linked_participants: form.linked_participants,
        };
        const res = await api.post('/roster/sil/create-shift', payload);
        newShiftId = res.data?.data?.id || res.data?.id || res.data?.data?.shift_id;
      } else {
        // 1:1 individual shift
        const payload = {
          participant_id: form.participant_id,
          service_id: form.service_id,
          shift_date: form.date,
          start_time: form.start_time,
          end_time: form.end_time,
          shift_type: 'individual',
          shift_period: form.shift_period === 'AUTO' ? null : form.shift_period,
          notes: form.notes,
          required_staff: 1,
        };
        const res = await api.post('/roster/shifts/create', payload);
        newShiftId = res.data?.data?.id || res.data?.id;
        if (form.staff_id && form.staff_id !== 'none' && newShiftId) {
          try { await api.post(`/roster/shifts/${newShiftId}/add-staff`, { staff_id: form.staff_id }); } catch {}
        }
      }

      // Persist activity rows (Option B + C)
      const activityErrors = [];
      if (newShiftId && activities.length) {
        for (let i = 0; i < activities.length; i++) {
          const a = activities[i];
          if (!a.service_catalog_id || !a.participant_id || !a.funding_plan_id) {
            activityErrors.push(`Row ${i + 1}: incomplete`);
            continue;
          }
          try {
            await api.post(`/shifts/${newShiftId}/service-allocations`, {
              service_catalog_id: a.service_catalog_id,
              participant_id: a.participant_id,
              funding_plan_id: a.funding_plan_id,
              allocation_start_time: a.allocation_start_time,
              allocation_end_time: a.allocation_end_time,
              allocation_order: i + 1,
              is_primary: i === 0,
            });
          } catch (e) {
            activityErrors.push(`Row ${i + 1}: ${e?.response?.data?.detail?.message || e?.response?.data?.detail || 'failed'}`);
          }
        }
      }

      if (activityErrors.length) {
        toast.warning(`Shift created but ${activityErrors.length} activity row(s) failed`, { description: activityErrors.join(' · ') });
      } else if (activities.length) {
        toast.success(`Shift created with ${activities.length} service activit${activities.length === 1 ? 'y' : 'ies'}`);
      } else {
        toast.success('Shift created');
      }
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to create shift');
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-shift-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4" /> Create Shift</DialogTitle>
          <DialogDescription>Create a new shift from the scheduling board</DialogDescription>
        </DialogHeader>

        {/* Shift Type segmented control */}
        <div className="grid grid-cols-3 gap-2" data-testid="shift-type-segmented">
          {SHIFT_TYPES.map(t => {
            const Icon = t.icon;
            const active = shiftType === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setShiftType(t.value)}
                className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-2 text-xs transition ${active ? 'border-primary bg-primary/10 text-primary font-semibold' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                data-testid={`shift-type-${t.value}`}
              >
                <Icon className="w-4 h-4" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground -mt-2">{SHIFT_TYPES.find(t => t.value === shiftType)?.hint}</p>

        {/* Centre Session — link out (creating one is non-trivial, defer to dedicated page) */}
        {shiftType === 'centre' && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs space-y-2" data-testid="centre-cta">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800">Centre sessions use a dedicated builder.</p>
                <p className="text-amber-700">Group day programs need venue, activity blocks, and per-participant attendance — too rich for a quick-create dialog.</p>
              </div>
            </div>
            <Button variant="outline" size="sm" data-testid="centre-cta-open"
              onClick={() => { onClose(); navigate('/admin/centre-sessions'); }}>
              Open Centre Sessions Builder <ExternalLink className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}

        {/* Common date/time row */}
        {shiftType !== 'centre' && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="shift-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Start</Label>
                <Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} data-testid="shift-start" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">End</Label>
                <Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} data-testid="shift-end" />
              </div>
            </div>

            {/* 1:1 participant */}
            {shiftType === 'individual' && (
              <div className="space-y-1">
                <Label className="text-xs">Participant</Label>
                <Select value={form.participant_id} onValueChange={v => setForm({ ...form, participant_id: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="shift-participant"><SelectValue placeholder="Select participant" /></SelectTrigger>
                  <SelectContent>{participants.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            {/* SIL house + residents (Option A + C) */}
            {shiftType === 'sil' && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">SIL House</Label>
                  <Select value={form.house_id} onValueChange={v => setForm({ ...form, house_id: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="shift-sil-house"><SelectValue placeholder="Select SIL house" /></SelectTrigger>
                    <SelectContent>
                      {houses.length === 0 && (
                        <div className="px-2 py-2 text-xs text-muted-foreground">No SIL houses found. Create one under Accommodation → Properties.</div>
                      )}
                      {houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name} {h.occupancy ? `(${h.occupancy}/${h.bed_count || '—'} beds)` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Support Ratio</Label>
                  <Select value={form.support_ratio} onValueChange={v => setForm({ ...form, support_ratio: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="shift-sil-ratio"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1:1">1:1 (one staff per resident)</SelectItem>
                      <SelectItem value="1:2">1:2</SelectItem>
                      <SelectItem value="1:3">1:3</SelectItem>
                      <SelectItem value="1:4">1:4</SelectItem>
                      <SelectItem value="2:1">2:1 (high-intensity, 2 staff per resident)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center justify-between">
                    <span>Residents <span className="text-muted-foreground">({(form.linked_participants || []).length}/{houseResidents.length} selected)</span></span>
                  </Label>
                  {houseResidents.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic px-2 py-1.5 border rounded">Select a house first to see residents.</p>
                  ) : (
                    <div className="border rounded p-2 max-h-32 overflow-y-auto space-y-1" data-testid="shift-sil-residents">
                      {houseResidents.map(r => {
                        const pid = r.participant_id || r.id;
                        const p = participants.find(pp => pp.id === pid);
                        const label = p ? `${p.first_name} ${p.last_name}` : (r.participant_name || pid);
                        const checked = (form.linked_participants || []).includes(pid);
                        return (
                          <label key={pid} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                            <Checkbox checked={checked} onCheckedChange={() => toggleResident(pid)} data-testid={`shift-sil-resident-${pid}`} />
                            <span>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Primary Service</Label>
                {(() => {
                  const ctxPid = shiftType === 'individual' ? form.participant_id : (form.linked_participants || [])[0];
                  const funded = fundedServices(ctxPid);
                  if (funded && funded.length > 0) {
                    return (
                      <button type="button"
                        onClick={() => setShowAllPrimary(s => !s)}
                        className="text-[10px] text-primary hover:underline"
                        data-testid="primary-service-toggle-all">
                        {showAllPrimary ? `Show funded only (${funded.length})` : `Show all (${services.length})`}
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
              {(() => {
                const ctxPid = shiftType === 'individual' ? form.participant_id : (form.linked_participants || [])[0];
                const funded = fundedServices(ctxPid);
                const list = (showAllPrimary || !funded || funded.length === 0) ? services : funded;
                const emptyFundedHint = funded && funded.length === 0 && ctxPid;
                return (
                  <Select value={form.service_id} onValueChange={v => setForm({ ...form, service_id: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="shift-service"><SelectValue placeholder="Select service" /></SelectTrigger>
                    <SelectContent>
                      {emptyFundedHint && !showAllPrimary && (
                        <div className="px-2 py-1.5 text-[10px] text-amber-700 bg-amber-50">
                          No services funded for this participant. Click "Show all" above to override.
                        </div>
                      )}
                      {list.map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.code}{s.code && s.name ? ` (${s.code})` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                );
              })()}
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Assign Staff (optional)</Label>
              <Select value={form.staff_id} onValueChange={v => setForm({ ...form, staff_id: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="shift-staff"><SelectValue placeholder="No staff (open shift)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Open Shift)</SelectItem>
                  {sortedStaff.map(s => {
                    const key = s.user_id || s.id;
                    const status = availEnabled ? statusMap[key]?.status : null;
                    return (
                      <SelectItem key={s.id} value={key}>
                        <span className="inline-flex items-center gap-2 w-full">
                          <span className="truncate">{s.full_name}</span>
                          {status ? <AvailabilityStatusBadge status={status} /> : null}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Shift Period</Label>
              <Select value={form.shift_period} onValueChange={v => setForm({ ...form, shift_period: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="shift-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AUTO">Auto-detect{autoPreview ? ` (${autoPreview})` : ''}</SelectItem>
                  {SHIFT_PERIODS.map(p => <SelectItem key={p} value={p}>{p.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">Sleep-Over must be selected manually — it is never auto-classified.</p>
            </div>

            {/* Option B: multi-service activity rows */}
            <div className="space-y-1 border-t pt-3" data-testid="shift-activities-section">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold">Service Activities <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                  onClick={addActivity}
                  disabled={activityParticipants.length === 0}
                  data-testid="shift-add-activity">
                  <Plus className="w-3 h-3 mr-1" /> Add activity
                </Button>
              </div>
              {activityParticipants.length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic">
                  {shiftType === 'individual' ? 'Pick a participant first to add activities.' : 'Select residents above to add activities for them.'}
                </p>
              ) : activities.length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic">
                  No activities added — shift will use the primary service only. Add activities to split into multiple invoice lines (Phase 3F multi-service billing).
                </p>
              ) : (
                <div className="space-y-2">
                  {activities.map((a, idx) => {
                    const plans = a.participant_id ? (planCache[a.participant_id] || []) : [];
                    return (
                      <div key={a._key} className="border rounded p-2 space-y-1.5 bg-slate-50" data-testid={`shift-activity-row-${idx}`}>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="text-[10px]">Activity {idx + 1}</Badge>
                          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600"
                            onClick={() => removeActivity(a._key)}
                            data-testid={`shift-activity-remove-${idx}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <Input type="time" value={a.allocation_start_time}
                            onChange={e => updateActivity(a._key, { allocation_start_time: e.target.value })}
                            className="h-7 text-xs" data-testid={`shift-activity-start-${idx}`} />
                          <Input type="time" value={a.allocation_end_time}
                            onChange={e => updateActivity(a._key, { allocation_end_time: e.target.value })}
                            className="h-7 text-xs" data-testid={`shift-activity-end-${idx}`} />
                        </div>
                        {shiftType === 'sil' && (
                          <Select value={a.participant_id}
                            onValueChange={v => updateActivity(a._key, { participant_id: v, funding_plan_id: '' })}>
                            <SelectTrigger className="h-7 text-xs" data-testid={`shift-activity-participant-${idx}`}>
                              <SelectValue placeholder="Participant for this activity" />
                            </SelectTrigger>
                            <SelectContent>
                              {activityParticipants.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                        {shiftType === 'individual' && form.participant_id && !a.participant_id && null}
                        {(() => {
                          const funded = fundedServices(a.participant_id, a.funding_plan_id || null);
                          const showAll = !!showAllActivity[a._key];
                          const list = (showAll || !funded || funded.length === 0) ? services : funded;
                          const emptyFundedHint = funded && funded.length === 0 && a.participant_id;
                          return (
                            <>
                              <div className="flex items-center justify-between -mb-1">
                                <Label className="text-[10px] text-muted-foreground">Service</Label>
                                {funded && funded.length > 0 && (
                                  <button type="button"
                                    onClick={() => setShowAllActivity(prev => ({ ...prev, [a._key]: !prev[a._key] }))}
                                    className="text-[10px] text-primary hover:underline"
                                    data-testid={`shift-activity-service-toggle-${idx}`}>
                                    {showAll ? `Funded only (${funded.length})` : `Show all (${services.length})`}
                                  </button>
                                )}
                              </div>
                              <Select value={a.service_catalog_id}
                                onValueChange={v => updateActivity(a._key, { service_catalog_id: v })}>
                                <SelectTrigger className="h-7 text-xs" data-testid={`shift-activity-service-${idx}`}>
                                  <SelectValue placeholder="Select service" />
                                </SelectTrigger>
                                <SelectContent>
                                  {emptyFundedHint && !showAll && (
                                    <div className="px-2 py-1.5 text-[10px] text-amber-700 bg-amber-50">
                                      No services funded{a.funding_plan_id ? ' under this plan' : ' for this participant'}.
                                    </div>
                                  )}
                                  {list.map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.code}{s.code && s.name ? ` (${s.code})` : ''}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </>
                          );
                        })()}
                        <Select value={a.funding_plan_id}
                          onValueChange={v => updateActivity(a._key, { funding_plan_id: v })}>
                          <SelectTrigger className="h-7 text-xs" data-testid={`shift-activity-funding-${idx}`}>
                            <SelectValue placeholder="Funding plan" />
                          </SelectTrigger>
                          <SelectContent>
                            {plans.length === 0 && (
                              <div className="px-2 py-1 text-[10px] text-muted-foreground">
                                {a.participant_id ? 'No funding plans for this participant.' : 'Pick a participant first.'}
                              </div>
                            )}
                            {plans.map(p => <SelectItem key={p.id} value={p.id}>{fundingPlanLabel(p)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="text-xs" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {shiftType !== 'centre' && (
            <Button onClick={handleCreate} disabled={creating} data-testid="create-shift-submit">
              {creating ? 'Creating...' : `Create ${SHIFT_TYPES.find(t => t.value === shiftType)?.label}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
